import type { CSSProperties } from "react";
import { monthGrid, venueTextColor } from "@web/theme/theme";

/** `dot`: a small venue-colored dot (tours) instead of a bar. */
export type MonthBarStyle = "solid" | "striped" | "outline" | "grey" | "dot";
export type MonthBar = { key: string; from: string; to: string; color?: string | null; style: MonthBarStyle; label: string };

export type MonthGridProps = {
  /** YYYY-MM */
  month: string;
  bars: readonly MonthBar[];
  selected?: string | null;
  today: string;
  onSelect: (date: string) => void;
  /** Bars shown per cell on phones before "+n" (desktop shows more). */
  maxBars?: number;
};

const DOW = ["S", "M", "T", "W", "T", "F", "S"];
const pad = (n: number) => String(n).padStart(2, "0");

/** The 6×7 cells of a month, Sunday-first, with leading/trailing days of the neighbours. */
export function monthCells(month: string): { date: string; inMonth: boolean }[] {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const start = new Date(first);
  start.setUTCDate(1 - first.getUTCDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    return { date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`, inMonth: d.getUTCMonth() === m - 1 };
  });
}

const barClass: Record<MonthBarStyle, string> = { solid: "v365-bar", striped: "v365-bar-stripes", outline: "v365-bar-outline", grey: monthGrid.barGrey, dot: "v365-bar" };

/** Month calendar: bookings solid in venue color, holds striped, pending outline, blocks grey (SPEC §6.1 item 6). */
export function MonthGrid({ month, bars, selected, today, onSelect, maxBars = 3 }: MonthGridProps) {
  const cells = monthCells(month);
  return (
    <div className="flex flex-col gap-1">
      <div className={monthGrid.header}>
        {DOW.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div role="group" aria-label="Choose a calendar day" className={monthGrid.grid}>
        {cells.map(({ date, inMonth }) => {
          const dayBars = bars.filter((b) => b.from <= date && b.to >= date);
          const shown = dayBars.slice(0, maxBars);
          const isSel = selected === date;
          return (
            <button
              key={date}
              type="button"
              aria-pressed={isSel}
              aria-label={`${date}${dayBars.length ? `, ${dayBars.length} item${dayBars.length > 1 ? "s" : ""}` : ""}`}
              onClick={() => onSelect(date)}
              className={`${monthGrid.cell} ${inMonth ? "" : monthGrid.cellMuted} ${isSel ? monthGrid.cellSelected : ""}`}
            >
              <span className={`${monthGrid.day} ${date === today ? monthGrid.dayToday : ""}`}>{Number(date.slice(8))}</span>
              {shown.map((b) => (
                <span key={b.key} title={b.label} style={b.color ? ({ "--bar": b.color, "--bar-contrast": venueTextColor(b.color) } as CSSProperties) : undefined} className={`${b.style === "dot" ? monthGrid.dot : monthGrid.bar} ${barClass[b.style]}`}>
                  {b.style !== "dot" && <span className={`${monthGrid.barLabel} ${b.style === "solid" ? "" : "text-text"}`}>{b.label}</span>}
                </span>
              ))}
              {dayBars.length > maxBars && <span className={monthGrid.more}>+{dayBars.length - maxBars}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
